// The Feedback tab on a skill (views/Skills/SkillsPage.vue): what colleagues
// sent through update_skill from Claude, ChatGPT or Alice's chat (see
// functions/modules/skills/feedback.js). Pure, so it is unit-tested in
// tests/unit/skillFeedback.test.js.

export const KIND_LABELS = {
  feedback: "Feedback",
  change_request: "Change request",
  update: "Proposed revision",
};

export const RATING_LABELS = {
  worked: "Worked",
  partly: "Partly worked",
  did_not_work: "Didn't work",
};

export const SOURCE_LABELS = {
  mcp: "via Claude/ChatGPT",
  chat: "in Alice chat",
  communal: "in team chat",
};

// Open items first, newest first within each group.
export function splitFeedback(items) {
  const list = [...(items || [])].sort(
    (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
  );
  return {
    open: list.filter((f) => f.status === "open"),
    handled: list.filter((f) => f.status !== "open"),
  };
}

// What a handled item says about how it was handled.
export function resolutionLabel(item) {
  if (!item) return "";
  if (item.status === "applied") {
    return item.appliedInVersion
      ? `Applied in v${item.appliedInVersion}`
      : "Applied";
  }
  if (item.status === "done") return "Handled";
  if (item.status === "declined") return "Declined";
  return "";
}

// Line diff (longest common subsequence) between the skill body on screen and
// a proposed one: [{ type: "same" | "del" | "add", text }]. Skill bodies are
// a few hundred lines at most, so the quadratic table is fine.
export function lineDiff(before, after) {
  const a = String(before || "").split("\n");
  const b = String(after || "").split("\n");
  const n = a.length;
  const m = b.length;
  const lcs = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: "del", text: a[i++] });
    } else {
      out.push({ type: "add", text: b[j++] });
    }
  }
  while (i < n) out.push({ type: "del", text: a[i++] });
  while (j < m) out.push({ type: "add", text: b[j++] });
  return out;
}

// The diff for display: runs of more than 2 × `context` unchanged lines fold
// into one { type: "skip", count } row, keeping `context` lines around each
// change.
export function foldDiff(rows, context = 2) {
  const out = [];
  let run = [];
  const flush = (atStart, atEnd) => {
    const keepHead = atStart ? 0 : context;
    const keepTail = atEnd ? 0 : context;
    if (run.length > keepHead + keepTail + 1) {
      out.push(...run.slice(0, keepHead));
      out.push({ type: "skip", count: run.length - keepHead - keepTail });
      out.push(...run.slice(run.length - keepTail));
    } else {
      out.push(...run);
    }
    run = [];
  };
  rows.forEach((row) => {
    if (row.type === "same") {
      run.push(row);
      return;
    }
    flush(out.length === 0, false);
    out.push(row);
  });
  flush(out.length === 0, true);
  return out;
}

// The first message for the AI drawer ("Process with AI"): the open items, so
// the Skill Creator drafts one revision that addresses them.
export function feedbackPrompt(items) {
  const lines = (items || []).map((f, i) => {
    const head = [KIND_LABELS[f.kind] || f.kind];
    if (f.rating) head.push(RATING_LABELS[f.rating] || f.rating);
    if (f.version) head.push(`on v${f.version}`);
    let text = `${i + 1}. [${head.join(", ")}] ${f.summary}`;
    if (f.details) text += `\n   ${f.details.replace(/\n/g, "\n   ")}`;
    if (f.proposed && f.proposed.description) {
      text += `\n   Proposed description: ${f.proposed.description}`;
    }
    if (f.proposed && f.proposed.body) {
      text += `\n   Proposed body:\n${f.proposed.body}`;
    }
    return text;
  });
  return (
    "Colleagues who used this skill sent the feedback below. Revise the " +
    "skill so it addresses what holds up, and tell me in a few lines which " +
    "points you took over, which you left out and why. Keep what works, " +
    "keep the skill in the language it is written in, and don't add client " +
    "or project specifics.\n\n" +
    lines.join("\n\n")
  );
}
